'use client'

import { useMemo, useState } from 'react'
import { HugeiconsIcon } from '@hugeicons/react'
import { Alert01Icon, MagicWand01Icon } from '@hugeicons-pro/core-stroke-rounded'
import { Button } from '@/components/ui/button'
import type { ProposedPatch } from '@/contexts/ChatContext'
import type { CommandFormState } from '@/components/automations/CommandFormPage'
import { DiffRow } from './DiffRow'

type PatchPreviewCardProps = {
  proposal: ProposedPatch
  currentForm: CommandFormState
  onApply: (fields: string[]) => void
  onDiscard: () => void
}

export function PatchPreviewCard({ proposal, currentForm, onApply, onDiscard }: PatchPreviewCardProps) {
  const fields = useMemo(() => Object.keys(proposal.patch), [proposal.patch])
  const [selected, setSelected] = useState(() => new Set(fields))
  const hasErrors = (proposal.errors?.length ?? 0) > 0
  const selectedFields = fields.filter((field) => selected.has(field))

  return (
    <section className="rounded-2xl border border-atrium-border bg-atrium-background text-atrium-black shadow-sm">
      <header className="flex items-start gap-3 border-b border-atrium-border px-5 py-4">
        <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-atrium-content text-accent-600">
          <HugeiconsIcon icon={MagicWand01Icon} className="size-4" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold">{proposal.summary}</h3>
          {proposal.reasoning && <p className="mt-1 text-sm leading-5 text-atrium-black/60">{proposal.reasoning}</p>}
        </div>
        <Button type="button" variant="ghost" size="sm" onClick={onDiscard}>Discard</Button>
      </header>

      <div className="space-y-3 px-5 py-4">
        {proposal.warnings.length > 0 && (
          <div className="rounded-lg border border-amber-500/40 bg-amber-50 px-3 py-2 text-sm text-amber-950">
            {proposal.warnings.join(' ')}
          </div>
        )}

        {(proposal.dangerousToolsAdded?.length ?? 0) > 0 && (
          <div className="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-50 px-3 py-2 text-sm text-amber-950">
            <HugeiconsIcon icon={Alert01Icon} className="mt-0.5 size-4 shrink-0" />
            <span>This will add tools that can mutate live data: {proposal.dangerousToolsAdded?.join(', ')}</span>
          </div>
        )}

        {hasErrors ? (
          <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            {proposal.errors?.join(' ')}
          </div>
        ) : null}

        {fields.length > 0 ? (
          <div className="space-y-2">
            {fields.map((field) => (
              <DiffRow
                key={field}
                field={field}
                oldValue={currentForm[field as keyof CommandFormState]}
                newValue={proposal.patch[field as keyof CommandFormState]}
                checked={selected.has(field)}
                onToggle={() => {
                  setSelected((prev) => {
                    const next = new Set(prev)
                    if (next.has(field)) next.delete(field)
                    else next.add(field)
                    return next
                  })
                }}
              />
            ))}
          </div>
        ) : (
          <p className="text-sm text-atrium-black/60">No valid fields were returned.</p>
        )}
      </div>

      {!hasErrors && fields.length > 0 && (
        <footer className="flex flex-wrap justify-end gap-2 border-t border-atrium-border px-5 py-3">
          <Button type="button" variant="ghost" onClick={onDiscard}>Discard</Button>
          <Button type="button" variant="outline" disabled={selectedFields.length === 0} onClick={() => onApply(selectedFields)}>
            Apply selected
          </Button>
          <Button type="button" onClick={() => onApply(fields)}>Apply all</Button>
        </footer>
      )}
    </section>
  )
}
